import React, { useEffect, Fragment } from "react";
import { styled } from '@mui/material/styles';
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Chip from "@mui/material/Chip";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";

import bannerImg from "../../../assets/images/waves-pano.jpg";
import earthquakeImg from "../../../assets/images/earthquake.png";
import floodImg from "../../../assets/images/flood.png";
import hurricaneImg from "../../../assets/images/hurricane.png";
import tsunamiImg from "../../../assets/images/tsunami.png";


import handleGA, { gaEvents } from "../../googleAnalytics";

const PREFIX = 'Help';

const classes = {
  root: `${PREFIX}-root`,

  // migrating styles to emotion: App.theme styles no longer imported from file using useStyles
  banner: `${PREFIX}-banner`,
  bannerTextBox: `${PREFIX}-bannerTextBox`,
  bannerTextBoxHeader: `${PREFIX}-bannerTextBoxHeader`,
  bannerTextBoxSubHeader: `${PREFIX}-bannerTextBoxSubHeader`,
  bannerTextBoxButton: `${PREFIX}-bannerTextBoxButton`,
  infoIcon: `${PREFIX}-infoIcon`,

  container: `${PREFIX}-container`,
  image: `${PREFIX}-image`,
  accordionRoot: `${PREFIX}-accordionRoot`,
  accordionHeading: `${PREFIX}-accordionHeading`,
  link: `${PREFIX}-link`,
  chipContainer: `${PREFIX}-chipContainer`,
  flexColumn: `${PREFIX}-flexColumn`,
  invisible: `${PREFIX}-invisible`,
  chip: `${PREFIX}-chip`,
  chipButton: `${PREFIX}-chipButton`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.root}`]: {
    display: "block",
    background: "#fafafa",
    [theme.breakpoints.down("sm")]: {
      width: "100vw",
    },
  },

  [`& .${classes.banner}`]: {
    position: "relative",
    maxWidth: '100vw'
    },

  [`& .${classes.bannerTextBox}`]: {
    position: "absolute",
    left: "12vw",
    top: "5rem",
    display: "flex",
    flexDirection: "column",
    maxWidth: "20rem",
    background: "#fff",
    borderRadius: "5px",
    padding: "2rem",
    lineHeight: "1.5",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "16rem",
      top: "0",
      right: "0",
      bottom: "0",
      left: "0",
      margin: "2rem auto",
    },
  },

  [`& .${classes.bannerTextBoxHeader}`]: {
    color: "#0078a9",
    fontWeight: "bold",
    fontSize: "1.3rem",
    },

  [`& .${classes.bannerTextBoxSubHeader}`]: {
	margin: "1rem 0 0 0",
    },

  [`& .${classes.bannerTextBoxButton}`]: {
    background: "#0078a9",
    color: "#fff",
    textTransform: "none",
    display: "flex",
    alignSelf: "end",
    marginTop: "1.5em",
    "&:hover": {
      background: "#006c98",
    },
  },

  [`& .${classes.infoIcon}`]: {
    color: '#0078a9',
    fontSize: '0.9rem',
    margin: '0 0.25rem'
    },

  [`& .${classes.container}`]: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    maxWidth: "54%",
    margin: "3em auto",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      margin: "3em auto",
    },
  },

  [`& .${classes.image}`]: {
    maxHeight: "400px",
    minWidth: "100vw",
    objectFit: "cover",
    objectPosition: "center",
    [theme.breakpoints.down("sm")]: {
      maxHeight: "550px",
    },
  },

  [`& .${classes.accordionRoot}`]: {
    width: "100%",
  },

  [`& .${classes.accordionHeading}`]: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: "bold",
  },

  [`& .${classes.link}`]: {
    color: "#005287",
    fontWeight: "bold",
  },

  [`& .${classes.chipContainer}`]: {
    margin: "1rem 0",
  },

  [`& .${classes.flexColumn}`]: {
    flexDirection: "column",
  },

  [`& .${classes.invisible}`]: {
    visibility: "none",
  },

  [`& .${classes.chip}`]: {},

  [`& .${classes.chipButton}`]: {
    "& .MuiChip-root": {
      "& :hover": {
        boxShadow: "none",
      },
    },
    margin: "0.25rem 0.5rem 0 0",
    // marginRight: "0.5rem",
    borderRadius: "50px",
    // overflow: "hidden",
    padding: 0,
    "& :hover": {
      boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.05)",
      borderRadius: "50px",
      cursor: "pointer",
    },
    textTransform: "capitalize",
    [theme.breakpoints.down("sm")]: {
      margin: "0.3rem 0",
    },
  }
}));

const Help = ({ updateBreadcrumbs }) => {

  const api = process.env.REACT_APP_API;
  const dataDictionary = {
    hetDataDictionary:
    "https://www.fema.gov/sites/default/files/documents/fema_hazus_export_tool_data_dictionary_1.pdf"
  };
  const dataDictionaryAlt = {
    earthquake: `${api}static/help/EQDataDictionary.pdf`,
    hurricane: `${api}static/help/HUDataDictionary.pdf`,
    flood: `${api}static/help/FLDataDictionary.pdf`,
    tsunami: `${api}static/help/TSDataDictionary.pdf`,
  };

  const HazardIcon = ({ img }) => {
    return (
      <Fragment>
        <img
          style={{
            height: "1.2rem",
            marginLeft: "0.5rem",
            pointerEvents: "none",
          }}
          src={img}
          alt="hazard icon"
        ></img>
      </Fragment>
    );
  };

  const onChangeAccordion = (event, expanded, label) => {
    // Google Analytics
    expanded && handleGA(gaEvents.VIEW_HELP, label);
  };

  const onClickDataDictionary = (event, label) => {
    // Google Analytics
    handleGA(gaEvents.DOWNLOAD_DATA_DICTIONARY, label);
  };

  const onClickExternalResource = (event, label) => {
    handleGA(gaEvents.NAVIGATE_TO_EXTERNAL_RESOURCE, label);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Google Analytics
    handleGA(gaEvents.PAGE_VIEW, window.location.pathname);
  }, []);

  useEffect(() => {
    document.title = "Explore our Help Section | Hazus Loss Library";
    document.getElementById('description').content = "Learn more about Hazus by reviewing Frequently Asked Questions, which cover Hazus Package regions, how to search the HLL, information on data formats, and more.";
  })

  return (
    <Root className={classes.root}>
      <div className={classes.banner}>
        <div className={classes.bannerTextBox}>
          <span className={classes.bannerTextBoxHeader}>Find Help</span>
          <h3 className={classes.bannerTextBoxSubHeader}>
            Thanks for visiting the Hazus Loss Library!
          </h3>
          <p>
            The Hazus Loss Library is intended to help users find natural hazard
            loss modeling information without having to do the modeling.
          </p>
        </div>
        <img
          src={bannerImg}
          alt="a sky after storm"
          className={classes.image}
        ></img>
      </div>
      <div className={classes.container}>
        <div className={classes.accordionRoot}>
          <Accordion
            onChange={(e, exp) =>
              onChangeAccordion(
                e,
                exp,
                "How to get information from the Hazus Loss Library"
              )
            }
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
              <Typography className={classes.accordionHeading}>
                How to get information from the Hazus Loss Library
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Access the library of historic events, planning scenarios, and
                locations from the{" "}
                <Link to="/library" className={classes.link}>
                  library page
                </Link>
                . Each result in the library has a details page where the
                analyses for that result can be viewed. Each analysis has an
                option at the bottom to show the downloads. These downloads
                provide access to information including Hazus Package Regions
                (HPRs), summary result spreadsheets, reports, geospatial data,
                and links to hazard data. Learn more about how to search and get 
                the most out of the HLL <a href="https://www.fema.gov/sites/default/files/documents/fema_hll_how_to_guide_final.pdf" 
                target="_blank" aria-label="HLL How to Guide" rel="noreferrer" className={classes.link}>here</a>. 
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            onChange={(e, exp) =>
              onChangeAccordion(e, exp, "What is a Hazus Package Region (HPR)")
            }
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
              <Typography className={classes.accordionHeading}>
                What is a Hazus Package Region (HPR)?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                A Hazus Package Region (HPR) is an exported Hazus Study region,
                with the file extension .hpr, that contains the inventory,
                hazard data, and results from a Hazus scenario. These files can
                be directly imported into Hazus using the <b>Import a region</b>{" "}
                option in the Hazus startup window.
                <br />
                <br />
                Hazus users also have the option to import an HPR and connect to
                a SQL database in ArcGIS, or HPRs can be unzipped outside of
                Hazus. Hazus can only support importing HPRs created by the
                latest version of Hazus or one version back. For instance, Hazus
                5.1 can support importing HPRs created by Hazus 5.0 or Hazus
                4.2.3. For more information on HPRs please check out the
                following Hazus YouTube videos:
                <br />
                <br />
                <a
                  href="https://www.youtube.com/watch?v=OSWSWhnCCeo&list=PL720Kw_OojlKuAIdn9cAFT1RqCQDE6Nye&index=19"
                  target="_blank"
                  aria-label="vist Hazus YouTube"
                  rel="noreferrer"
                  onClick={(e) =>
                    onClickExternalResource(e, "Restore a Legacy HPR")
                  }
                >
                  Restore a Legacy Hazus Package Region (HPR)
                </a>
                <br />
                <a
                  href="https://www.youtube.com/watch?v=IYCi9bb0Cq8&list=PL720Kw_OojlKuAIdn9cAFT1RqCQDE6Nye&index=14"
                  target="_blank"
                  aria-label="vist Hazus YouTube"
                  rel="noreferrer"
                  onClick={(e) =>
                    onClickExternalResource(
                      e,
                      "Connect to a SQL Database in ArcGIS"
                    )
                  }
                >
                  Connect to a SQL Database in ArcGIS
                </a>
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            onChange={(e, exp) =>
              onChangeAccordion(
                e,
                exp,
                "How do I search for results in the Hazus Loss Library"
              )
            }
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
              <Typography className={classes.accordionHeading}>
                How do I search for results in the Hazus Loss Library?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                The Hazus Loss Library lets users search by two types of
                results: events (historic and scenario) and locations.
                <br />
                <br />
                Events are distinguished as either historic, meaning any natural
                hazard that actually occurred, or as scenario, which is a
                modeled natural hazard that hasn’t occurred. Scenarios have two
                subtypes, deterministic and probabilistic. Deterministic events
                are hypothesized scenarios based on authoritative hazard data
                from partner agencies, such as a large earthquake. Probabilistic
                events are based on a return period, with the most common
                example being the 100-year flood (1% annual chance flood).
                Searching for an event will give you all analyses associated
                with that event.
                <br />
                <br />
                Locations include every state, county, and city/place in the
                United States. When selecting a location result, you will find
                every analysis that intersects that location boundary,
                regardless if the analysis is a historic event or a planning
                scenario.

                More information on how to search the HLL and download results can be found in our How-To-Guide <a href="https://www.fema.gov/sites/default/files/documents/fema_hll_how_to_guide_final.pdf" target="_blank" aria-label="HLL How to Guide" rel="noreferrer"  className={classes.link}>here</a>.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            onChange={(e, exp) =>
              onChangeAccordion(e, exp, "Are missing Hazus Package Regions (HPR) available?")
            }
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
			  <Typography className={classes.accordionHeading}>
                Are missing Hazus Package Regions (HPR) available?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                  Yes, all Hazus HPRs are archived by the Hazus program. If an 
                  HPR is missing from the data downloads please email{" "}
                  <span className={classes.link}>
                    <a href="mailto:fema-hazus-support@fema.dhs.gov?subject='HLL - Missing HPR'">
                      fema-hazus-support@fema.dhs.gov</a> 
                  </span>{" "}
                  with the subject line <b>HLL - Missing
                  HPR</b> and identify the scenario title of the missing HPR in the 
                  email body.
                </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            onChange={(e, exp) =>
              onChangeAccordion(e, exp, "Download Metadata")
            }
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
              <Typography className={classes.accordionHeading}>
                Download Metadata
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                To find information on the data fields used in each Hazus model,
                please see the Hazus Data Dictionaries below. Please also check
                the downloads section on the analysis details page to see if
                there are additional metadata files for download.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            onChange={(e, exp) =>
              onChangeAccordion(e, exp, "Download Data Dictionaries")
            }
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
              <Typography className={classes.accordionHeading}>
                Download Data Dictionaries
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.flexColumn}>
              <Typography>
                View our data dictionaries to find metadata and descriptions
                about HLL downloads generated by the Hazus Export Tool.
              </Typography>
              <div className={classes.chipContainer}>
                <Button
                  className={classes.chipButton}
                  target="_blank"
                  href={dataDictionary.hetDataDictionary}
                  onClick={(e) => onClickDataDictionary(e, "Hazus Export Tool Data Dictionary")}
                  download
                >
                <Chip
                  className={classes.chip}
                 //   icon={<HazardIcon img={earthquakeImg} />}
                  label="Hazus Export Tool Data Dictionary"
                  />
                </Button>
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion
            onChange={(e, exp) =>
              onChangeAccordion(e, exp, "What Does Hazus Version 6.1* Mean?")
            }
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
              <Typography className={classes.accordionHeading}>
                What Does Hazus Version 6.1* Mean?
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.flexColumn}>
              <Typography>
              A few issues were discovered in Hazus 6.1 that have been fixed 
              in a later-released version, Hazus 6.1*. The first issue occurred 
              where users of ShakeMap scenarios had low losses to pipelines as a 
              result of an incorrect unit conversion. The second issue was that 
              new IBC era building design level non-structural fragility curves 
              had incorrect parameter values resulting in losses that were 
              slightly lower than would be estimated with the corrected values.  
              With the parameter corrections, losses increase, on average, about 
              1% in California, but with the potential for larger differences 
              in high hazard areas with IBC era construction. HLL events with 
              "Hazus Version 6.1*" include the fix that corrects these fragility 
              values and low ShakeMap PGV values. 
              Please reach out to {" "}
                <span className={classes.link}>
                  <a href="mailto:fema-hazus-support@fema.dhs.gov?subject='HLL Issue'">
                    fema-hazus-support@fema.dhs.gov</a> 
                </span>{" "} 
              for the fix for 
              your Hazus 6.1 installation.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            onChange={(e, exp) => onChangeAccordion(e, exp, "Report An Issue")}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
              <Typography className={classes.accordionHeading}>
                Report an Issue
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Did you find an issue? We would like to get it resolved as soon
                as we can. Please email{" "}
                <span className={classes.link}>
                  <a href="mailto:fema-hazus-support@fema.dhs.gov?subject='HLL Issue'">
                    fema-hazus-support@fema.dhs.gov</a> 
                </span>{" "}
                with the subject line <b>HLL Issue</b> to report an issue.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            onChange={(e, exp) =>
              onChangeAccordion(e, exp, "Learn More About Hazus")
            }
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
              <Typography className={classes.accordionHeading}>
                Learn More About Hazus
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Learn more about Hazus. Hazus methodology is open-source and
                freely available through our User and Technical Manuals{" "}
                <a
                  className={classes.link}
                  target="_blank"
                  href="https://www.fema.gov/flood-maps/tools-resources/flood-map-products/hazus/user-technical-manuals"
                  rel="noreferrer"
                  onClick={(e) =>
                    onClickExternalResource(e, "Hazus Methodology")
                  }
                >
                  here
                </a>
                . For tutorials in using Hazus, check out our{" "}
                <a
                  className={classes.link}
                  target="_blank"
                  href="https://www.youtube.com/playlist?list=PL720Kw_OojlKuAIdn9cAFT1RqCQDE6Nye"
                  rel="noreferrer"
                  onClick={(e) => onClickExternalResource(e, "Hazus YouTube")}
                >
                  YouTube Videos
                </a>
                .
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            onChange={(e, exp) => onChangeAccordion(e, exp, "Disclaimer")}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2a-content"
              id="panel2a-header"
            >
              <Typography className={classes.accordionHeading}>
                Disclaimer
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                The estimates of social and economic impacts contained in the
                Hazus Loss Library were produced using{" "}
                <a
                  className={classes.link}
                  target="_blank"
                  href="https://www.fema.gov/flood-maps/tools-resources/flood-map-products/hazus/user-technical-manuals"
                  rel="noreferrer"
                  onClick={(e) =>
                    onClickExternalResource(e, "Hazus Technical Manuals")
                  }
                >
                  Hazus loss estimation methodologies
                </a>
                , which are based on peer reviewed scientific methods and sound
                engineering knowledge. There are uncertainties inherent in any
                loss estimate technique. Therefore, significant differences may
                exist between the modeled results contained in the Hazus Loss
                Library and the actual social and economic losses following a
                natural hazard. Nationwide inventory and demographic data used
                as inputs in Hazus are, in many cases, not as accurate as
                available local data. If you decide to download the Hazus
                inventory or results data and substitute it with local data, you
                assume responsibility for the accuracy of the data and any
                resulting loss analyses. Please visit the{" "}
                <a
                  className={classes.link}
                  target="_blank"
                  href="https://www.fema.gov/flood-maps/tools-resources/flood-map-products/hazus/resources"
                  rel="noreferrer"
                  onClick={(e) => onClickExternalResource(e, "Hazus Resources")}
                >
                  "Hazus Resources"
                </a>{" "}
                page if you’d like to learn more about how to conduct your own
                custom Hazus risk assessment.
                <br />
                <br />
                There are no license terms of use or restrictions for the Hazus
                Loss Library analyses. Hazus tools, data, and all associated
                information are available for public use.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
    </Root>
  );
};

export default Help;
